import { parseRef } from '@/lib/utils/reference-utils';
import { normalizeBookName } from '@/lib/utils/bible-utils';

export interface FlattenedCrossReference {
    /** The local verse where the footnote lives (e.g., "1:1") */
    ref: string;
    /** The normalized target reference (e.g., "Psalms 90:2") */
    targetRef: string;
    /** Optional associated footnote text */
    footnoteText?: string;
    /** Numeric sort key based on local verse position */
    sortKey: number;
}

// Regex: matches a book prefix (optionally starting with a digit for numbered books)
// followed by whitespace and a chapter:verse reference.
// Examples: "Ps 90:2", "2Kg 17:13", "1Co 11:9"
const BOOK_REF_RE = /^(\d*[A-Za-z].+?)\s+([\d:,\-]+)$/;

/**
 * Parses raw CSB cross-reference footnotes into a flat, normalized list.
 *
 * Handles:
 * - Book name normalization (e.g., "Ps" → "Psalm")
 * - Continuation references (e.g., "Ps 60; 69; 80" = Psalm 60, 69, 80)
 * - Local references with no book (inherit from passage)
 * - Numbered book names (e.g., "2Kg", "1Co")
 * - LXX/MT manuscript suffixes
 * - En-dash normalization
 */
export function parseCrossReferences(
    rawFootnotes: TranslationFootnote[],
    passageBook: string,
    passageRef: string
): FlattenedCrossReference[] {
    const result: FlattenedCrossReference[] = [];

    for (const fn of rawFootnotes) {
        if (!fn.c) continue;

        // Extract local verse reference (e.g., "1:1") from "csb:Genesis:1:1"
        const parts = fn.r.split(':');
        const localVerse = parts.length >= 4
            ? `${parts[2]}:${parts[3]}`
            : passageRef.split(' ').pop() || '';

        // Sort key based on local verse
        const localRange = parseRef(parts.length >= 4 ? `${parts[2]}:${parts[3]}` : undefined);
        const sortKey = localRange ? localRange.chapterStart * 1000 + localRange.verseStart : 0;

        // Clean footnote text once
        const footnoteText = fn.t ? fn.t.replace(/\*[ir]/g, '') : undefined;

        // Split by ';' and strip '*ID' suffixes
        const refs = fn.c.split(';');

        // Track last-seen book for continuation references.
        // CSB format: "Ps 60; 69; 80" → Psalm 60, Psalm 69, Psalm 80
        let lastSeenBook = '';

        for (const raw of refs) {
            let ref = raw.trim().replace(/\*\d+$/, '');

            // Strip manuscript suffixes (e.g., "Is 9:6 LXX" → "Is 9:6")
            ref = ref.replace(/\s+LXX$/i, '').replace(/\s+MT$/i, '');

            // Normalize en-dashes to hyphens
            ref = ref.replace(/–/g, '-');

            // Detect book prefix
            const bookMatch = ref.match(BOOK_REF_RE);
            if (bookMatch) {
                lastSeenBook = bookMatch[1];
            } else if (/^\d/.test(ref)) {
                // Bare number or chapter:verse → inherit book from previous ref or passage
                ref = `${lastSeenBook || passageBook} ${ref}`;
            }

            // Normalize the book name to canonical form
            const normalizeMatch = ref.match(/^(.+?)\s+([\d:,\-]+)$/);
            if (normalizeMatch) {
                ref = `${normalizeBookName(normalizeMatch[1])} ${normalizeMatch[2]}`;
            }

            result.push({ ref: localVerse, targetRef: ref, footnoteText, sortKey });
        }
    }

    return result.sort((a, b) => a.sortKey - b.sortKey);
}
